import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import { useShellSelection, type ActionCardRenderer } from '@cowbell/moo';
import { useAllow } from '../../../hooks/useAllow';
import { AsyncTaskActionCard } from '../../components/action-cards/AsyncTaskActionCard';
import ChartCard from '../../components/action-cards/ChartCard';
import ChecklistCard from '../../components/action-cards/ChecklistCard';
import { ConnectIntegrationPromptShim } from '../../components/action-cards/ConnectIntegrationPromptShim';
import EmailDraftCard from '../../components/action-cards/EmailDraftCard';
import EntityProfileCard from '../../components/action-cards/EntityProfileCard';
import FormApprovalCard from '../../components/action-cards/FormApprovalCard';
import FormConfirmationCard from '../../components/action-cards/FormConfirmationCard';
import FormInputCard from '../../components/action-cards/FormInputCard';
import NoteDraftCard from '../../components/action-cards/NoteDraftCard';
import QuickReplyPicker from '../../components/action-cards/QuickReplyPicker';
import QuoteDraftCard from '../../components/action-cards/QuoteDraftCard';
import CodeCitationCard from '../../components/action-cards/CodeCitationCard';
import SummaryCard from '../../components/action-cards/SummaryCard';
import TimelineCard from '../../components/action-cards/TimelineCard';
import actionCardStyles from '../../components/action-cards/ActionCard.module.css';
import type {
  Action as V1Action,
  AssistantMessage as V1AssistantMessage,
  ChartAction,
  ChecklistAction,
  EmailDraftAction,
  EntityProfileAction,
  FormApprovalAction,
  FormConfirmationAction,
  FormInputAction,
  FormNoteDraftAction,
  FormQuoteDraftAction,
  NavigationAction,
  ConnectIntegrationAction,
  QuickReplyAction,
  SummaryAction,
  CodeCitationAction,
  TimelineAction,
} from '../../types';

/** Build the V1 AssistantMessage shape that the V1 cards expect from the
 *  slot's narrower (action, messageId, correlationId) inputs. Cards only
 *  read `messageId`, `correlationId`, and (occasionally) `actions`, so the
 *  rest are inert defaults. */
function toV1Message(
  action: V1Action,
  messageId: string,
  correlationId: string | null
): V1AssistantMessage {
  return {
    role: 'assistant',
    content: '',
    thinkingBlock: null,
    segments: [],
    sources: [],
    actions: [action],
    correlationId: correlationId ?? '',
    messageId,
    isStreaming: false,
  };
}

/** Shell conversation is only a fallback for older persisted actions that
 *  omit `conversationId` on the payload — the Redis run is keyed by the
 *  conversation it was started in. */
function AsyncTaskActionCardShim({
  data,
  isLastMessage,
  onSendMessage,
}: Readonly<{
  data: unknown;
  isLastMessage?: boolean;
  onSendMessage?: (message: string, skill?: string) => void;
}>) {
  const { conversationId: shellConversationId } = useShellSelection();
  return (
    <AsyncTaskActionCard
      data={data}
      conversationIdFallback={shellConversationId}
      isLastMessage={isLastMessage}
      onSendMessage={onSendMessage}
    />
  );
}

/** V2-native NavigationPrompt. The V1 component depends on V1's AskMooContext
 *  (`setActiveAgentId`); rather than wrap the V2 host in a V1 provider, this
 *  shim talks to `useShellSelection` directly. Layout / icon choices mirror
 *  V1 verbatim. */
function NavigationPromptShim({
  action,
  originatingQuery,
}: Readonly<{ action: NavigationAction; originatingQuery?: string }>) {
  const { selectAgent } = useShellSelection();
  const history = useHistory();
  // OMNI-637: creating agents requires account:cowbell (was root:root under
  // OMNI-184); hide the shortcut for personas without it (parity with V1's
  // NavigationPrompt).
  const canCreate = useAllow(['account:cowbell']);

  const handleClick = useCallback(() => {
    if (action.action === 'switch-agent' && action.agentId) {
      // Switch + replay (V1 parity): forward `replayQuery` so the original
      // user question is queued for the newly selected agent and auto-answered
      // without the user retyping. Some backend paths (read-only refusal /
      // referral card) omit `replayQuery`; fall back to the originating user
      // turn that produced this card so the question still carries over
      // (OMNI-580; mirrors the V1 OMNI-442 fallback).
      const replayQuery = action.replayQuery?.trim()
        ? action.replayQuery
        : originatingQuery;
      selectAgent(action.agentId, replayQuery);
    } else if (action.action === 'open-moo-studio' && canCreate) {
      history.push('/moo/studio/edit');
    }
  }, [action, originatingQuery, selectAgent, history, canCreate]);

  if (action.action === 'open-moo-studio' && !canCreate) return null;

  return (
    <div className={actionCardStyles.navigationButton}>
      <Button
        size="xs"
        variant="light"
        leftSection={
          <FontAwesomeIcon
            icon={
              action.action === 'open-moo-studio'
                ? faWandMagicSparkles
                : faArrowRight
            }
            size="sm"
          />
        }
        onClick={handleClick}
      >
        {action.label}
      </Button>
    </div>
  );
}

/** V1 cards registered as `ActionCardRenderer`s the V2 library can resolve
 *  via `MooChatConfig.actionCards`. The slot looks up by `(type, subType)`;
 *  type-only entries (chart, email, async-task) match when no exact
 *  subType match exists. */
export function makeActionCardRenderers(): ActionCardRenderer[] {
  return [
    {
      type: 'form',
      subType: 'confirmation',
      render: ({ data, messageId, correlationId }) => (
        <FormConfirmationCard
          action={data as FormConfirmationAction}
          message={toV1Message(data as V1Action, messageId, correlationId)}
        />
      ),
    },
    {
      type: 'form',
      subType: 'approval',
      render: ({ data, messageId, correlationId }) => (
        <FormApprovalCard
          action={data as FormApprovalAction}
          message={toV1Message(data as V1Action, messageId, correlationId)}
        />
      ),
    },
    {
      type: 'form',
      subType: 'input',
      render: ({ data, messageId, correlationId }) => (
        <FormInputCard
          action={data as FormInputAction}
          message={toV1Message(data as V1Action, messageId, correlationId)}
        />
      ),
    },
    {
      type: 'form',
      subType: 'quote-draft',
      render: ({ data, messageId, correlationId }) => (
        <QuoteDraftCard
          action={data as FormQuoteDraftAction}
          message={toV1Message(data as V1Action, messageId, correlationId)}
        />
      ),
    },
    {
      type: 'form',
      subType: 'note-draft',
      render: ({ data, messageId, correlationId, isLastMessage }) => (
        <NoteDraftCard
          action={data as FormNoteDraftAction}
          message={toV1Message(data as V1Action, messageId, correlationId)}
          isLastMessage={isLastMessage}
        />
      ),
    },
    {
      type: 'chart',
      render: ({ data }) => <ChartCard action={data as ChartAction} />,
    },
    {
      type: 'prompt',
      subType: 'quick-reply',
      render: ({ data, onSendMessage, isLastMessage, followupUserText }) => (
        <QuickReplyPicker
          action={data as QuickReplyAction}
          onSendMessage={onSendMessage ?? undefined}
          isLastMessage={isLastMessage}
          followupUserText={followupUserText}
        />
      ),
    },
    {
      type: 'prompt',
      subType: 'navigation',
      render: ({ data, originatingQuery }) => (
        <NavigationPromptShim
          action={data as NavigationAction}
          originatingQuery={originatingQuery}
        />
      ),
    },
    {
      type: 'prompt',
      subType: 'connect-integration',
      render: ({ data }) => (
        <ConnectIntegrationPromptShim
          action={data as ConnectIntegrationAction}
        />
      ),
    },
    {
      type: 'info',
      subType: 'entity-profile',
      render: ({ data }) => (
        <EntityProfileCard action={data as EntityProfileAction} />
      ),
    },
    {
      type: 'info',
      subType: 'checklist',
      render: ({ data }) => <ChecklistCard action={data as ChecklistAction} />,
    },
    {
      type: 'info',
      subType: 'timeline',
      render: ({ data }) => <TimelineCard action={data as TimelineAction} />,
    },
    {
      type: 'info',
      subType: 'summary',
      render: ({ data }) => <SummaryCard action={data as SummaryAction} />,
    },
    {
      type: 'info',
      subType: 'code-citation',
      render: ({ data }) => (
        <CodeCitationCard action={data as CodeCitationAction} />
      ),
    },
    // Type-only: ActionCardSlot falls back to type match when no exact
    // (type, subType) renderer exists, so one entry covers progress,
    // proposal, and older persisted actions that omit subType.
    {
      type: 'async-task',
      render: ({ data, isLastMessage, onSendMessage }) => (
        <AsyncTaskActionCardShim
          data={data}
          isLastMessage={isLastMessage}
          onSendMessage={onSendMessage ?? undefined}
        />
      ),
    },
    {
      type: 'email',
      render: ({ data, isLastMessage, onSendMessage }) => (
        <EmailDraftCard
          action={data as EmailDraftAction}
          isLastMessage={isLastMessage}
          onSendMessage={onSendMessage ?? undefined}
        />
      ),
    },
  ];
}
